import { useMutation } from '@tanstack/react-query';
import type { CountryCode } from 'libphonenumber-js';
import { lazy, Suspense, useCallback, useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { lazily } from 'react-lazily';
import { isPossiblePhoneNumber, type Value } from 'react-phone-number-input';

import {
	type PostErrorFullRegisterResponse,
	postFullRegister,
	type PostFullRegisterRequest,
} from 'services/idm';

import { useCaseInsensitiveParam } from 'hooks/use-case-insensitive-params';
import { useAppTranslation } from 'hooks/use-translates';

import { ApiErrorBlock } from '@login/components/api-error-block';
import { BirthPlaceBlock } from '@login/components/birth-place-block';
import { AddressFields } from '@login/components/fields/address-fields';
import { DateOfBirthFields } from '@login/components/fields/date-of-birth-fields';
import { MarketingOptIn } from '@login/components/fields/marketing-opt-in';
import { PasswordFields } from '@login/components/fields/password-fields';
import { FormBox } from '@login/components/form-box';
import { FormHeader } from '@login/components/form-header';
import { MarketingDisclaimer } from '@login/components/marketing-disclaimer';
import { RichText } from '@login/components/rich-text';
import { TurnstileWidget } from '@login/components/turnstile-widget';
import { apiErrorHandler } from '@login/helpers';
import type { Country } from '@login/helpers/arrays';
import { useApiRedirect } from '@login/hooks/use-api-redirect';
import { useIdmFormErrors } from '@login/hooks/use-idm-form-errors';
import { useScrollToFirstError } from '@login/hooks/use-scroll-to-first-error';
import { useTurnstile } from '@login/hooks/use-turnstile';
import { EMAIL_PATTERN } from '@login/lib/constants';
import {
	FULL_REGISTER_ERROR_KEYS,
	FULL_REGISTER_FIELD_ORDER,
} from '@login/patterns/form-messages';

import styles from './fullregister.module.scss';

const Button = lazy(() => import('core/generic/elements/button'));
const Divider = lazy(() => import('core/generic/elements/divider'));
const InfoRequiredFields = lazy(
	() => import('@login/components/info-required-fields/info-required-fields')
);
const { Insert, Phone } = lazily(() => import('core/generic/elements/form'));

type FullRegisterFormUrls = {
	registerUrl?: string;
	signInUrl?: string;
	forgottenPasswordUrl?: string;
	privacyPolicyUrl?: string;
	supportName?: string;
	siteName?: string;
	minimumAge?: string;
};

type ChannelSpecificFields = {
	showCountyOfBirthForSpecifiedCountries: string[];
	showAndRequireCityOfBirth?: boolean;
	showAndRequireCountryOfBirth?: boolean;
	showAndRequireCountyOfBirth?: boolean;
};

type FullRegisterFormProps = {
	urls: FullRegisterFormUrls | null;
	marketingMessage: string;
	disclaimerMessage: string;
	token?: string | null;
	countries: Country[] | null;
	channelSpecificFields: ChannelSpecificFields | undefined;
	showChallengeForm?: boolean;
};

const DEFAULT_COUNTRY: CountryCode = 'IT';
const EMPTY_COUNTRIES: Country[] = [];

// The Phone element requires an onChange prop, but the value is driven by
// react-hook-form's `control`. Declared once so the prop stays stable.
const NOOP_PHONE_CHANGE = () => undefined;

export const FullRegisterForm = ({
	urls,
	token,
	marketingMessage,
	disclaimerMessage,
	countries,
	channelSpecificFields,
	showChallengeForm = false,
}: FullRegisterFormProps) => {
	const { i18n } = useTranslation();
	const { t } = useAppTranslation(urls);
	const { goToRedirect } = useApiRedirect();
	const returnUrl = useCaseInsensitiveParam('returnUrl');
	const turnstile = useTurnstile(showChallengeForm);

	const apiErrors = useIdmFormErrors({
		t,
		messageKeys: FULL_REGISTER_ERROR_KEYS,
	});

	const methods = useForm<PostFullRegisterRequest>({ mode: 'onBlur' });
	const {
		control,
		handleSubmit,
		register,
		formState: { errors },
	} = methods;

	const countryList = countries ?? EMPTY_COUNTRIES;
	const selectedCountry = useWatch({ control, name: 'address.countryCode' });

	const defaultCountryCode = useMemo<CountryCode>(() => {
		const match = countryList.find(
			(country) => country.shortName === selectedCountry
		);
		return (match?.shortName as CountryCode) ?? DEFAULT_COUNTRY;
	}, [countryList, selectedCountry]);

	const minimumAge = urls?.minimumAge ? Number(urls.minimumAge) : undefined;

	useScrollToFirstError({
		errors,
		apiFieldErrors: apiErrors.fields,
		hasBanner: !!apiErrors.banner,
		fieldOrder: FULL_REGISTER_FIELD_ORDER,
	});

	// `mutate`, not `mutateAsync`: failures are handled in onError, and an
	// awaited rejection here would surface as an unhandled promise rejection.
	const { mutate: submitFullRegister, isPending: isSubmitting } = useMutation(
		{
			mutationKey: ['full-register', i18n.language, token, returnUrl],
			mutationFn: (formData: PostFullRegisterRequest) =>
				postFullRegister({
					data: {
						...formData,
						returnUrl,
						cfToken: turnstile.token,
					},
					lang: i18n.language,
					token,
				}),
			onSuccess: (response) => {
				if (response.action === 'Redirect' && response.redirectUrl) {
					goToRedirect({
						action: response.action,
						url: response.redirectUrl,
					});
					return;
				}
				turnstile.reset();
			},
			onError: (err) => {
				const error = err as unknown as PostErrorFullRegisterResponse;
				apiErrorHandler(error, i18n.language);
				apiErrors.setFromApiError(error);
				turnstile.reset();
			},
		}
	);

	const handleOnSubmit = useCallback(
		(formData: PostFullRegisterRequest) => {
			if (turnstile.isPending) {
				apiErrors.setBanner(
					t(
						'common.turnstile_required',
						{},
						'Please complete the security check'
					)
				);
				return;
			}

			apiErrors.clear();
			submitFullRegister(formData);
		},
		[turnstile.isPending, apiErrors, t, submitFullRegister]
	);

	const dobLabels = useMemo(
		() => ({
			legend: t('fullregister.dob_label_full'),
			hint: t('fullregister.dob_help_full'),
			message: t('fullregister.dob_message_full'),
			dayPlaceholder: t('fullregister.day_placeholder_full'),
			monthPlaceholder: t('fullregister.month_placeholder_full'),
			yearPlaceholder: t('fullregister.year_placeholder_full'),
			dayRequired: t('fullregister.day_required_error_full'),
			monthRequired: t('fullregister.month_required_error_full'),
			yearRequired: t('fullregister.year_required_error_full'),
			tooYoung: t('fullregister.too_young_error_full'),
		}),
		[t]
	);

	const passwordLabels = useMemo(
		() => ({
			password: t('fullregister.password_label_full'),
			passwordConfirm: t('fullregister.confirm_password_label_full'),
			complexityError: t('fullregister.password_complexity_error_full'),
			nonEnglishError: t('validation.password_english_characters'),
			matchesUsernameError: t(
				'fullregister.password_matches_username_error_full'
			),
			mustMatchError: t('fullregister.password_must_match_error_full'),
		}),
		[t]
	);

	const addressLabels = useMemo(
		() => ({
			country: t('fullregister.country_label_full'),
			countryPlaceholder: t('fullregister.country_placeholder_full'),
			countryRequired: t('fullregister.country_required_error_full'),
			addressLine1: t('fullregister.address_line1_label_full'),
			addressLine1Required: t(
				'fullregister.address_line1_required_error_full'
			),
			addressLine2: t('fullregister.address_line2_label_full'),
			addressLine3: t('fullregister.address_line3_label_full'),
			city: t('fullregister.city_label_full'),
			cityRequired: t('fullregister.city_required_error_full'),
			postcode: t('fullregister.postcode_label_full'),
			postcodeRequired: t('fullregister.postcode_required_error_full'),
			postcodeInvalid: (postcodeSample: string) =>
				t('fullregister.postcode_invalid_error_full', {
					postcodeSample,
				}),
		}),
		[t]
	);

	const marketingLabels = useMemo(
		() => ({
			yes: t('fullregister.yes_label_full'),
			no: t('fullregister.no_label_full'),
			partnerMessage: t('fullregister.partner_message_full'),
			optOutMessage: t('fullregister.opt_out_message_full'),
			transactionalMessage: t(
				'fullregister.transactional_email_message_full'
			),
			requiredError: t('fullregister.opt_in_required_error_full'),
		}),
		[t]
	);

	const showBirthPlace =
		!!channelSpecificFields?.showAndRequireCityOfBirth ||
		!!channelSpecificFields?.showAndRequireCountryOfBirth;

	const bannerMessage = apiErrors.banner || errors.birthDate?.message || '';

	return (
		<Suspense fallback={null}>
			<FormProvider {...methods}>
				<form
					onSubmit={handleSubmit(handleOnSubmit)}
					id="form-profile"
					noValidate
				>
					<FormBox>
						<FormHeader headerTitle={t('common.register_tab')} />
						<div className={styles['form-box-content']}>
							<ApiErrorBlock apiErrorMessage={bannerMessage} />

							<InfoRequiredFields />

							<DateOfBirthFields
								labels={dobLabels}
								minimumAge={minimumAge}
								showRequiredMarker
								resolveError={apiErrors.getFieldError}
							/>

							{!errors.birthDate?.message && (
								<>
									<Insert
										{...register('email', {
											required: t(
												'fullregister.email_required_error_full'
											),
											pattern: {
												value: EMAIL_PATTERN,
												message: t(
													'fullregister.email_invalid_error_full'
												),
											},
										})}
										type="email"
										defaultValue={''}
										id="email"
										label={t(
											'fullregister.email_label_full'
										)}
										required={true}
										error={apiErrors.getFieldError(
											errors.email?.message,
											'Email'
										)}
										errorIcon="AlertCircleFilled"
									/>

									<PasswordFields
										labels={passwordLabels}
										resolveError={apiErrors.getFieldError}
									/>

									<Divider />

									<div className={styles.registerDataSection}>
										<Insert
											{...register('firstName', {
												required: t(
													'fullregister.first_name_required_error_full'
												),
											})}
											defaultValue={''}
											id="firstName"
											label={t(
												'fullregister.first_name_label_full'
											)}
											required={true}
											error={apiErrors.getFieldError(
												errors.firstName?.message,
												'FirstName'
											)}
											errorIcon="AlertCircleFilled"
										/>
										<Insert
											{...register('lastName', {
												required: t(
													'fullregister.last_name_required_error_full'
												),
											})}
											defaultValue={''}
											id="lastName"
											label={t(
												'fullregister.last_name_label_full'
											)}
											required={true}
											error={apiErrors.getFieldError(
												errors.lastName?.message,
												'LastName'
											)}
											errorIcon="AlertCircleFilled"
										/>
									</div>

									<AddressFields
										labels={addressLabels}
										countries={countryList}
										resolveError={apiErrors.getFieldError}
									/>

									<Phone
										rules={{
											required: t(
												'fullregister.mobile_phone_required_error_full'
											),
											validate: (value: string) =>
												isPossiblePhoneNumber(value) ||
												t(
													'fullregister.mobile_phone_invalid_error_full'
												),
										}}
										label={t(
											'fullregister.mobile_phone_label_full'
										)}
										id="mobilePhone"
										name="mobilePhone"
										error={apiErrors.getFieldError(
											errors.mobilePhone?.message,
											'MobilePhone'
										)}
										errorIcon="AlertCircleFilled"
										defaultValue={'' as Value}
										defaultCountry={defaultCountryCode}
										required={true}
										control={control}
										onChange={NOOP_PHONE_CHANGE}
									/>

									<Divider />

									{showBirthPlace &&
										channelSpecificFields && (
											<>
												<div
													className={
														styles.registerDataSection
													}
												>
													<BirthPlaceBlock
														countriesWithCounty={
															channelSpecificFields.showCountyOfBirthForSpecifiedCountries
														}
														countries={countryList}
														errors={errors}
														channelSpecificFields={
															channelSpecificFields
														}
													/>
												</div>
												<Divider />
											</>
										)}

									<MarketingOptIn
										labels={marketingLabels}
										marketingMessage={marketingMessage}
									/>

									<MarketingDisclaimer
										message={disclaimerMessage}
										privacyPolicyUrl={
											urls?.privacyPolicyUrl
										}
									/>

									{showChallengeForm && (
										<TurnstileWidget
											ref={turnstile.ref}
											action="full-register"
											language={i18n.language}
											onSuccess={turnstile.onSuccess}
											onExpire={turnstile.onExpire}
											onError={turnstile.onError}
										/>
									)}

									<Button
										type="submit"
										label={t(
											'fullregister.register_button_full'
										)}
										isLoading={isSubmitting}
										disabled={
											isSubmitting || turnstile.isPending
										}
										variant="primary"
										fullWidth={true}
										className={styles.submitButton}
										dimension="large"
									/>

									<div
										className={`body-small medium ${styles.signinText}`}
									>
										<RichText
											text={t('common.sigin_link')}
											linkClassName={styles['signin-now']}
										/>
									</div>
								</>
							)}
						</div>
					</FormBox>
				</form>
			</FormProvider>
		</Suspense>
	);
};

export default FullRegisterForm;
